import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './UserModal.css';
import {
    X, User, Mail, Phone, Globe, Calendar, FileText, BarChart3,
    CheckCircle, XCircle, Activity, AlertCircle, Briefcase,
    Banknote, Award, Target, GraduationCap, ArrowRight
} from 'lucide-react';

import ReportModal from './ReportModal';

const ADMIN_BACKEND_URL = process.env.REACT_APP_API_URL;
const MICROSERVICE_API_URL = process.env.REACT_APP_LOGIN_APP_API_BASE;

// Helper components
const DetailItem = ({ label, value, icon }) => (
  <div className="detail-item">
    <div className="detail-item-label">{icon}{label}</div>
    <span className="detail-item-value">{value || 'N/A'}</span>
  </div>
);

const ReportSummaryCard = ({ report, userName, onViewReport }) => {
  const getPrimaryRecommendation = (report) => {
    if (!report?.aiResults?.recommendations?.[0]) {
      return 'No specific recommendation';
    }
    const firstRec = report.aiResults.recommendations[0];
    return firstRec.college || `Recommendation for ${firstRec.country}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div className="report-summary-card">
        <div className="card-header">
            <span className="package-name">{report.packageName || 'General'} Package</span>
            {/* <span className="report-date">{formatDate(report.generatedAt)}</span> */}
        </div>
        <div className="card-body">
            <h3>{getPrimaryRecommendation(report)}</h3>
            <p className="for-user">For: {userName}</p>
        </div>
        <div className="card-footer">
            <button onClick={() => onViewReport(report)}>
            View Full Report <ArrowRight size={16} />
            </button>
        </div>
    </div>
  );
};

// Main UserModal Component
const UserModal = ({ user, onClose , setIsLoading }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [userProfile, setUserProfile] = useState(null);
  const [studentForms, setStudentForms] = useState([]);
  const [studentReports, setStudentReports] = useState([]);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.userId || !user?._id) {
        setError("Invalid user data: Missing required IDs.");
        if (setIsLoading) setIsLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('adminToken');
        if (!token) throw new Error('Authentication token not found');
        const authHeaders = { 'Authorization': `Bearer ${token}` };

        const [profileRes, formsRes, reportsRes] = await Promise.all([
          fetch(`${MICROSERVICE_API_URL}/api/user/${user.userId}`, { headers: authHeaders }),
          fetch(`${ADMIN_BACKEND_URL}/api/forms/${user.userId}`, { headers: authHeaders }),
          fetch(`${ADMIN_BACKEND_URL}/api/reports/${user.userId}`, { headers: authHeaders })
        ]);
        
        if (!profileRes.ok) throw new Error(`Failed to fetch user profile (Status: ${profileRes.status})`);
        const profileData = await profileRes.json();
        setUserProfile(profileData.user || profileData);

        if (formsRes.status === 404) {
            setStudentForms([]);
        } else if (!formsRes.ok) {
            throw new Error(`Failed to fetch student forms (Status: ${formsRes.status})`);
        } else {
            setStudentForms(await formsRes.json());
        }
        
        if (reportsRes.status === 404) {
            setStudentReports([]);
        } else if (!reportsRes.ok) {
            throw new Error(`Failed to fetch student reports (Status: ${reportsRes.status})`);
        } else {
            setStudentReports(await reportsRes.json());
        }

      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err.message);
      } finally {
        if (setIsLoading) setIsLoading(false);
      }
    };

    fetchData();
  }, [user, setIsLoading]);

  const calculateAge = (dobString) => {
    if (!dobString) return 'N/A';
    try {
        const birthDate = new Date(dobString);
        return Math.abs(new Date(Date.now() - birthDate.getTime()).getUTCFullYear() - 1970);
    } catch (error) { return 'N/A'; }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (error) {
    return (
        <div className="user-modal-overlay" onClick={onClose}>
            <div className="user-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-error-state">
                    <AlertCircle size={48} color="#ef4444" />
                    <h3>Error Loading Data</h3>
                    <p>{error}</p>
                    <button className="close-modal-button" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
  }

  const fullName = userProfile ? [userProfile.firstName, userProfile.lastName].filter(Boolean).join(' ') : (user?.fullName || 'Unknown User');
  const avatarInitial = fullName.charAt(0).toUpperCase();

  return (
    <>
     <Helmet>
        <title>{`Viewing Profile: ${fullName} | GN VISA Admin`}</title>
      </Helmet>
      <div className="user-modal-overlay" onClick={onClose}>
        <div className="user-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="user-modal-header">
            <div className="user-modal-title">
              <div className="user-avatar-large">{avatarInitial}</div>
              <div className="user-title-info">
                <h2>{fullName}</h2>
                <p>User ID: {user?.userId || 'N/A'}</p>
              </div>
            </div>
            <button className="close-button" onClick={onClose}><X size={24} /></button>
          </div>

          <div className="user-modal-tabs">
            <button className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`} onClick={() => setActiveTab('personal')}><User size={16} /> Personal Information</button>
            <button className={`tab-button ${activeTab === 'forms' ? 'active' : ''}`} onClick={() => setActiveTab('forms')}><FileText size={16} /> Student Form ({studentForms.length})</button>
            <button className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}><BarChart3 size={16} /> Reports ({studentReports.length})</button>
          </div>

          <div className="user-modal-body">
            {activeTab === 'personal' && userProfile && (
              <>
                <div className="info-section">
                  <h3 className="section-title"><User size={18} /> Personal Information</h3>
                  <div className="detail-grid">
                    <DetailItem label="Full Name" value={fullName} />
                    <DetailItem label="Email Address" value={userProfile.email} icon={<Mail size={14} />} />
                    <DetailItem label="Phone Number" value={userProfile.phone} icon={<Phone size={14} />} />
                    {/* <DetailItem label="Nationality" value={userProfile.nationality} icon={<Globe size={14} />} /> */}
                    <DetailItem label="Age" value={calculateAge(userProfile.dob)} />
                    <DetailItem label="Gender" value={userProfile.gender || 'Not Specified'} />
                  </div>
                </div>
                <div className="info-section">
                  <h3 className="section-title"><Activity size={18} /> Account Status</h3>
                  <div className="detail-grid">
                    <DetailItem label="Account Status" value={userProfile.isActive ? 'Active' : 'Inactive'} icon={userProfile.isActive ? <CheckCircle size={14} color="green"/> : <XCircle size={14} color="red"/>} />
                    <DetailItem label="Email Verification" value={userProfile.isVerified ? 'Verified' : 'Not Verified'} icon={userProfile.isVerified ? <CheckCircle size={14} color="green"/> : <XCircle size={14} color="red"/>} />
                    <DetailItem label="Last Login" value={formatDate(userProfile.lastLogin)} icon={<Calendar size={14} />} />
                    <DetailItem label="Registration Date" value={formatDate(userProfile.createdAt)} icon={<Calendar size={14} />} />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'forms' && (
              <div className="data-tab-content">
                {studentForms.length === 0 ? (
                  <div className="no-data-placeholder"><FileText size={48} /><h3>No Student Forms Found</h3><p>This user has not submitted any forms yet.</p></div>
                ) : (
                  studentForms.map((form) => (
                    <div key={form._id} className="data-card">
                      <h3 className="data-title">Form submitted on: {formatDate(form.createdAt)}</h3>
                      
                      {/* FIX: RESTORED THE MISSING FORM DETAILS RENDERING LOGIC HERE */}
                      <h4 className="data-subtitle"><User size={16}/> Personal Details</h4>
                      <div className="detail-grid">
                          <DetailItem label="First Name" value={form.firstName} />
                          <DetailItem label="Last Name" value={form.lastName} />
                          <DetailItem label="Country" value={form.country} />
                          <DetailItem label="State" value={form.state} />
                          <DetailItem label="City" value={form.city} />
                          <DetailItem label="Pincode" value={form.pincode} />
                      </div>

                      <h4 className="data-subtitle"><GraduationCap size={16}/> Academic Details</h4>
                      <div className="detail-grid">
                          <DetailItem label="10th Board %" value={form.board10th} />
                          <DetailItem label="12th Board %" value={form.board12th} />
                          <DetailItem label="12th Stream" value={form.stream12th} />
                          <DetailItem label="College Course" value={form.collegeCourse} />
                          <DetailItem label="College %" value={form.collegePercentage} />
                          <DetailItem label="Certifications" value={form.certifications} />
                      </div>

                      <h4 className="data-subtitle"><Target size={16}/> Goals</h4>
                      <div className="detail-grid">
                          <DetailItem label="Goal Country" value={form.goalCountry} />
                          <DetailItem label="Goal State" value={form.goalState} />
                          <DetailItem label="Goal University" value={form.goalUni} />
                      </div>

                      <h4 className="data-subtitle"><Award size={16}/> Test Scores</h4>
                      <div className="detail-grid">
                          <DetailItem label="IELTS/TOEFL Score" value={form.ieltsTofelScore} />
                          {form.hasOtherScore && <>
                              <DetailItem label="Other Exam" value={form.otherExamName} />
                              <DetailItem label="Other Exam Score" value={form.otherExamScore} />
                          </>}
                      </div>

                      <h4 className="data-subtitle"><Banknote size={16}/> Financials</h4>
                      <div className="detail-grid">
                          <DetailItem label="Budget" value={form.budget} />
                          <DetailItem label="Father's Experience" value={form.father?.experience} />
                          <DetailItem label="Father's Salary" value={form.father?.salary} />
                          <DetailItem label="Mother's Experience" value={form.mother?.experience} />
                          <DetailItem label="Mother's Salary" value={form.mother?.salary} />
                          <DetailItem label="Bank Balance (Liquid)" value={form.bankBalance?.liquid} />
                          <DetailItem label="Bank Balance (FD)" value={form.bankBalance?.fd} />
                      </div>

                      <h4 className="data-subtitle"><Briefcase size={16}/> Experience</h4>
                      <div className="detail-grid">
                          <DetailItem label="Job Experience" value={form.studentJobExperience} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div className="reports-tab-content">
                {studentReports.length === 0 ? (
                  <div className="no-data-placeholder"><BarChart3 size={48} /><h3>No Reports Generated</h3><p>No reports have been generated for this user yet.</p></div>
                ) : (
                  <div className="reports-grid">
                    {studentReports.map((report) => (
                      <ReportSummaryCard
                        key={report._id}
                        report={report}
                        userName={fullName}
                        onViewReport={setSelectedReport}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="user-modal-footer">
            <button className="close-modal-button" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
      
      {selectedReport && (
          <ReportModal 
              report={selectedReport}
              onClose={() => setSelectedReport(null)}
          />
      )}
    </>
  );
};

export default UserModal;





// import React, { useState, useEffect } from 'react';
// import { Helmet } from 'react-helmet-async';
// import './UserModal.css';
// import {
//     X, User, Mail, Phone, Calendar, FileText, BarChart3,
//     CheckCircle, XCircle, Activity, AlertCircle, Briefcase,
//     Banknote, Award, Target, GraduationCap, ArrowRight, Loader2
// } from 'lucide-react';

// // ReportModal component is imported but requires no changes.
// import ReportModal from './ReportModal';

// const ADMIN_BACKEND_URL = process.env.REACT_APP_API_URL;
// const MICROSERVICE_API_URL = process.env.REACT_APP_LOGIN_APP_API_BASE;

// // Helper component for displaying details consistently
// const DetailItem = ({ label, value, icon }) => (
//   <div className="detail-item">
//     <div className="detail-item-label">{icon}{label}</div>
//     <span className="detail-item-value">{value || 'N/A'}</span>
//   </div>
// );

// // Helper component for the report summary card in the list
// const ReportSummaryCard = ({ report, userName, onViewReportClick }) => {
//   const getPrimaryRecommendation = (report) => {
//     return report?.aiResults?.collegeAnalysis?.collegeName || 'No specific recommendation';
//   };

//   return (
//     <div className="report-summary-card">
//         <div className="card-header">
//             <span className="package-name">{report.packageName || 'General'} Package</span>
//         </div>
//         <div className="card-body">
//             <h3>{getPrimaryRecommendation(report)}</h3>
//             <p className="for-user">For: {userName}</p>
//         </div>
//         <div className="card-footer">
//             <button onClick={() => onViewReportClick(report)} disabled={!report.reportUrl}>
//                 View Full Report <ArrowRight size={16} />
//             </button>
//             {!report.reportUrl && <small className="no-url-warning">Report URL missing</small>}
//         </div>
//     </div>
//   );
// };


// // Main UserModal Component
// const UserModal = ({ user, onClose, setIsLoading }) => {
//   const [activeTab, setActiveTab] = useState('personal');
//   const [userProfile, setUserProfile] = useState(null);
//   const [studentForms, setStudentForms] = useState([]);
//   const [studentReports, setStudentReports] = useState([]);
//   const [error, setError] = useState(null);
  
//   // State for handling the detailed report modal
//   const [selectedReport, setSelectedReport] = useState(null);
//   const [isReportLoading, setIsReportLoading] = useState(false);

//   useEffect(() => {
//     // Fetches initial data for all tabs when the modal opens
//     const fetchData = async () => {
//       if (!user?.userId || !user?._id) {
//         setError("Invalid user data: Missing required IDs.");
//         if (setIsLoading) setIsLoading(false);
//         return;
//       }
//       try {
//         const token = localStorage.getItem('adminToken');
//         if (!token) throw new Error('Authentication token not found');
//         const authHeaders = { 'Authorization': `Bearer ${token}` };

//         const [profileRes, formsRes, reportsRes] = await Promise.all([
//           fetch(`${MICROSERVICE_API_URL}/api/user/${user.userId}`, { headers: authHeaders }),
//           fetch(`${ADMIN_BACKEND_URL}/api/forms/${user.userId}`, { headers: authHeaders }),
//           fetch(`${ADMIN_BACKEND_URL}/api/reports/${user.userId}`, { headers: authHeaders })
//         ]);

//         if (!profileRes.ok) throw new Error(`Failed to fetch user profile (Status: ${profileRes.status})`);
//         setUserProfile((await profileRes.json()).user);

//         if (formsRes.status === 404) setStudentForms([]);
//         else if (!formsRes.ok) throw new Error(`Failed to fetch student forms (Status: ${formsRes.status})`);
//         else setStudentForms(await formsRes.json());
        
//         if (reportsRes.status === 404) setStudentReports([]);
//         else if (!reportsRes.ok) throw new Error(`Failed to fetch student reports (Status: ${reportsRes.status})`);
//         else setStudentReports(await reportsRes.json());

//       } catch (err) {
//         console.error('Error fetching user data:', err);
//         setError(err.message);
//       } finally {
//         if (setIsLoading) setIsLoading(false);
//       }
//     };
//     fetchData();
//   }, [user, setIsLoading]);

//   // Fetches full report data from the URL before opening the detailed modal
//   const handleViewReportClick = async (reportSummary) => {
//     if (!reportSummary?.reportUrl) {
//       setError("Cannot view report: The report URL is missing.");
//       return;
//     }

//     setIsReportLoading(true);
//     setError(null);

//     try {
//       const response = await fetch(reportSummary.reportUrl);
//       if (!response.ok) {
//         throw new Error(`Failed to download report from URL. Status: ${response.status}`);
//       }
//       const fullReportData = await response.json();
//       setSelectedReport(fullReportData);
//     } catch (err) {
//       console.error('Error fetching full report:', err);
//       setError(err.message);
//     } finally {
//       setIsReportLoading(false);
//     }
//   };

//   const calculateAge = (dobString) => {
//     if (!dobString) return 'N/A';
//     try {
//         const birthDate = new Date(dobString);
//         return Math.abs(new Date(Date.now() - birthDate.getTime()).getUTCFullYear() - 1970);
//     } catch (error) { return 'N/A'; }
//   };
  
//   const formatDate = (dateString) => {
//     if (!dateString) return 'N/A';
//     return new Date(dateString).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
//   };

//   const fullName = userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : (user?.fullName || 'Unknown User');
//   const avatarInitial = fullName.charAt(0).toUpperCase();

//   return (
//     <>
//       <Helmet>
//         <title>{`Viewing Profile: ${fullName} | GN VISA Admin`}</title>
//       </Helmet>
//       <div className="user-modal-overlay" onClick={onClose}>
//         <div className="user-modal-content" onClick={(e) => e.stopPropagation()}>
//           <div className="user-modal-header">
//             <div className="user-modal-title">
//               <div className="user-avatar-large">{avatarInitial}</div>
//               <div className="user-title-info">
//                 <h2>{fullName}</h2>
//                 <p>User ID: {user?.userId || 'N/A'}</p>
//               </div>
//             </div>
//             <button className="close-button" onClick={onClose}><X size={24} /></button>
//           </div>

//           <div className="user-modal-tabs">
//             <button className={`tab-button ${activeTab === 'personal' ? 'active' : ''}`} onClick={() => setActiveTab('personal')}><User size={16} /> Personal Information</button>
//             <button className={`tab-button ${activeTab === 'forms' ? 'active' : ''}`} onClick={() => setActiveTab('forms')}><FileText size={16} /> Student Form ({studentForms.length})</button>
//             <button className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}><BarChart3 size={16} /> Reports ({studentReports.length})</button>
//           </div>
          
//           {error && <div className="modal-inline-error"><AlertCircle size={16}/> {error}</div>}

//           <div className="user-modal-body">
//             {/* === PERSONAL INFORMATION TAB === */}
//             {activeTab === 'personal' && userProfile && (
//               <>
//                 <div className="info-section">
//                   <h3 className="section-title"><User size={18} /> Personal Information</h3>
//                   <div className="detail-grid">
//                     <DetailItem label="Full Name" value={fullName} />
//                     <DetailItem label="Email Address" value={userProfile.email} icon={<Mail size={14} />} />
//                     <DetailItem label="Phone Number" value={userProfile.phone} icon={<Phone size={14} />} />
//                     <DetailItem label="Age" value={calculateAge(userProfile.dob)} />
//                     <DetailItem label="Gender" value={userProfile.gender || 'Not Specified'} />
//                   </div>
//                 </div>
//                 <div className="info-section">
//                   <h3 className="section-title"><Activity size={18} /> Account Status</h3>
//                   <div className="detail-grid">
//                     <DetailItem label="Account Status" value={userProfile.isActive ? 'Active' : 'Inactive'} icon={userProfile.isActive ? <CheckCircle size={14} color="green"/> : <XCircle size={14} color="red"/>} />
//                     <DetailItem label="Email Verification" value={userProfile.isVerified ? 'Verified' : 'Not Verified'} icon={userProfile.isVerified ? <CheckCircle size={14} color="green"/> : <XCircle size={14} color="red"/>} />
//                     <DetailItem label="Last Login" value={formatDate(userProfile.lastLogin)} icon={<Calendar size={14} />} />
//                     <DetailItem label="Registration Date" value={formatDate(userProfile.createdAt)} icon={<Calendar size={14} />} />
//                   </div>
//                 </div>
//               </>
//             )}

//             {/* === STUDENT FORM TAB (COMPLETE) === */}
//             {activeTab === 'forms' && (
//               <div className="data-tab-content">
//                 {studentForms.length === 0 ? (
//                   <div className="no-data-placeholder"><FileText size={48} /><h3>No Student Forms Found</h3><p>This user has not submitted any forms yet.</p></div>
//                 ) : (
//                   studentForms.map((form) => (
//                     <div key={form._id} className="data-card">
//                       <h3 className="data-title">Form submitted on: {formatDate(form.createdAt)}</h3>
                      
//                       <h4 className="data-subtitle"><User size={16}/> Personal Details</h4>
//                       <div className="detail-grid">
//                           <DetailItem label="First Name" value={form.firstName} />
//                           <DetailItem label="Last Name" value={form.lastName} />
//                           <DetailItem label="Country" value={form.country} />
//                           <DetailItem label="State" value={form.state} />
//                           <DetailItem label="City" value={form.city} />
//                           <DetailItem label="Pincode" value={form.pincode} />
//                       </div>

//                       <h4 className="data-subtitle"><GraduationCap size={16}/> Academic Details</h4>
//                       <div className="detail-grid">
//                           <DetailItem label="10th Board %" value={form.board10th} />
//                           <DetailItem label="12th Board %" value={form.board12th} />
//                           <DetailItem label="12th Stream" value={form.stream12th} />
//                           <DetailItem label="College Course" value={form.collegeCourse} />
//                           <DetailItem label="College %" value={form.collegePercentage} />
//                           <DetailItem label="Certifications" value={form.certifications} />
//                       </div>

//                       <h4 className="data-subtitle"><Target size={16}/> Goals</h4>
//                       <div className="detail-grid">
//                           <DetailItem label="Goal Country" value={form.goalCountry} />
//                           <DetailItem label="Goal State" value={form.goalState} />
//                           <DetailItem label="Goal University" value={form.goalUni} />
//                       </div>

//                       <h4 className="data-subtitle"><Award size={16}/> Test Scores</h4>
//                       <div className="detail-grid">
//                           <DetailItem label="IELTS/TOEFL Score" value={form.ieltsTofelScore} />
//                           {form.hasOtherScore && <>
//                               <DetailItem label="Other Exam" value={form.otherExamName} />
//                               <DetailItem label="Other Exam Score" value={form.otherExamScore} />
//                           </>}
//                       </div>

//                       <h4 className="data-subtitle"><Banknote size={16}/> Financials</h4>
//                       <div className="detail-grid">
//                           <DetailItem label="Budget" value={form.budget} />
//                           <DetailItem label="Father's Experience" value={form.father?.experience} />
//                           <DetailItem label="Father's Salary" value={form.father?.salary} />
//                           <DetailItem label="Mother's Experience" value={form.mother?.experience} />
//                           <DetailItem label="Mother's Salary" value={form.mother?.salary} />
//                           <DetailItem label="Bank Balance (Liquid)" value={form.bankBalance?.liquid} />
//                           <DetailItem label="Bank Balance (FD)" value={form.bankBalance?.fd} />
//                       </div>

//                       <h4 className="data-subtitle"><Briefcase size={16}/> Experience</h4>
//                       <div className="detail-grid">
//                           <DetailItem label="Job Experience" value={form.studentJobExperience} />
//                       </div>
//                     </div>
//                   ))
//                 )}
//               </div>
//             )}

//             {/* === REPORTS TAB === */}
//             {activeTab === 'reports' && (
//               <div className="reports-tab-content">
//                 {studentReports.length === 0 ? (
//                   <div className="no-data-placeholder"><BarChart3 size={48} /><h3>No Reports Generated</h3><p>No reports have been generated for this user yet.</p></div>
//                 ) : (
//                   <div className="reports-grid">
//                     {studentReports.map((report) => (
//                       <ReportSummaryCard
//                         key={report._id}
//                         report={report}
//                         userName={fullName}
//                         onViewReportClick={handleViewReportClick}
//                       />
//                     ))}
//                   </div>
//                 )}
//               </div>
//             )}
//           </div>

//           <div className="user-modal-footer">
//             <button className="close-modal-button" onClick={onClose}>Close</button>
//           </div>
//         </div>
//       </div>
      
//       {/* Loading overlay for fetching the full report */}
//       {isReportLoading && (
//         <div className="modal-loading-overlay">
//           <Loader2 className="spinner" size={48} />
//           <p>Loading Full Report...</p>
//         </div>
//       )}

//       {/* Renders the full report modal once data is fetched */}
//       {selectedReport && (
//           <ReportModal 
//               report={selectedReport}
//               onClose={() => setSelectedReport(null)}
//           />
//       )}
//     </>
//   );
// };

// export default UserModal;